import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  AlertIcon,
  AlertNotification,
  Button,
  ClockIcon,
  Loading,
  PaymentsIntegrationIcon,
  Text,
  colors,
  fontFamilies,
} from "@commonsku/styles";
import { oauth } from "../../../utils";
import { PAYMENTS_DASHBOARD_URL } from "../../../constants/CreditCardProviders";
import { ForwardScreen } from "./ForwardScreen";
import useForwardApplicationStatus from "../hooks/useForwardApplicationStatus";
import ForwardPaymentMethods from "./ForwardPaymentMethods";
import { AdminPaymentCreditCardFee } from "../payment/AdminPaymentCreditCardFee";

type Props = {
  data: Record<string, unknown>;
  save: (data: Record<string, unknown>, showToast?: boolean) => void;
  reloadData: () => Promise<void>;
};

const Banner = styled(AlertNotification)`
  margin-bottom: 24px;
`;

const BannerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const BannerDescription = styled(Text)`
  display: block;
  font-family: ${fontFamilies.regular};
`;

const Bold = styled.strong`
  font-family: ${fontFamilies.bold};
`;

const BoardingIframe = styled.iframe`
  width: 100%;
  height: 720px;
  border: none;
`;

const PROVIDER_NAMES: Record<string, string> = {
  STRIPE: "Stripe",
  CARDCONNECT: "Fiserv",
};

const SUPPORT_EMAIL = "support@commonsku.com";

const GENERIC_ERROR = "We couldn't reach commonsku payments. Please try again in a moment.";
const RATE_LIMIT_ERROR = "Rate limit exceeded. Please try again later.";

export const FORWARD_BOARDING_COMPLETE_MESSAGE = "commonsku:forward-boarding-complete";

export const FORWARD_GROUP = {
  DRAFT: "DRAFT",
  IN_REVIEW: "IN_REVIEW",
  APPROVED: "APPROVED",
  DECLINED: "DECLINED",
  CANCELLED: "CANCELLED",
  DELETED: "DELETED",
  UNKNOWN: "UNKNOWN",
} as const;

export function buildSupportMailto(applicationId?: string | null): string {
  const id = applicationId ?? "";
  const subject = `Help with my commonsku payments application | ${id}`;
  const body =
    `Hi commonsku support,\n\n` +
    `I need help with my commonsku payments application (ID: ${id}).\n\n` +
    `[Please describe your issue here]\n\n` +
    `Thank you.`;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function Forward({ data, save, reloadData }: Props) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boardingUri, setBoardingUri] = useState<string | null>(null);
  const [refreshingAfterBoarding, setRefreshingAfterBoarding] = useState(false);
  const [paymentCc, setPaymentCc] = useState(() => Boolean(data.forward_payment_cc ?? true));

  useEffect(() => {
    setPaymentCc(Boolean(data.forward_payment_cc ?? true));
  }, [data.forward_payment_cc]);

  const activeProvider = PROVIDER_NAMES[data?.credit_card_software as string];
  const applicationId = data?.forward_application_id as
    | string
    | null
    | undefined;

  const { loading, group, error: statusError, refetch } = useForwardApplicationStatus(applicationId);

  const handleBoardingComplete = useCallback(() => {
    setBoardingUri(null);
    setRefreshingAfterBoarding(true);
    setError(null);
    // Ensure forward_application_id is on company data (needed after a first-time create).
    void reloadData().catch(() => {
      setError(GENERIC_ERROR);
      setRefreshingAfterBoarding(false);
    });
  }, [reloadData]);

  // Once we have an application id after boarding, refresh status then leave the loader.
  useEffect(() => {
    if (!refreshingAfterBoarding || !applicationId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await refetch();
      } catch {
        if (!cancelled) {
          setError(GENERIC_ERROR);
        }
      } finally {
        if (!cancelled) {
          setRefreshingAfterBoarding(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshingAfterBoarding, applicationId, refetch]);

  useEffect(() => {
    if (!boardingUri) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if ((event.data as { type?: string } | null)?.type !== FORWARD_BOARDING_COMPLETE_MESSAGE) {
        return;
      }
      handleBoardingComplete();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [boardingUri, handleBoardingComplete]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      let uri: string | undefined;
      if (!applicationId) {
        // No application yet: create the business + boarding application. The
        // create response already carries a hosted boarding link, so show the
        // iframe from it directly. We must NOT reload company data here: a reload
        // flips companyGeneralData.loading, and CreditCardIntegration unmounts this
        // component while loading, which would discard the boardingUri we just set
        // and drop the user back on the connect button. The new application id is
        // refreshed later, once boarding completes, in handleBoardingComplete().
        const { json } = await oauth(
          "POST",
          "forward-payments/applications",
          {},
        );
        uri = (json as { application_link_uri?: string })?.application_link_uri;
      } else {
        // Existing DRAFT application: mint a fresh hosted boarding link.
        const { json } = await oauth(
          "POST",
          `forward-payments/applications/${applicationId}/link`,
          {},
        );
        uri = (json as { uri?: string })?.uri;
      }
      if (uri) {
        setBoardingUri(uri);
      } else {
        setError(GENERIC_ERROR);
      }
    } catch (e) {
      const err = e as {
        json?: { errors?: string[] };
        response?: { status: number; body: string };
      };

      const statusCode = err.response?.status;
      if (statusCode === 429) {
        setError(err.json?.errors?.[0] ?? RATE_LIMIT_ERROR);
      } else {
        setError(GENERIC_ERROR);
      }

    } finally {
      setConnecting(false);
    }
  };

  const startScreen = (
    <ForwardScreen
      icon={<PaymentsIntegrationIcon />}
      heading="Get paid without the hassle"
      description="Manage client payments, monitor transactions, and keep your books in sync. All from one place."
    >
      {error && <Banner alertType="error">{error}</Banner>}
      <Button
        variant="primary"
        size="medium"
        disabled={connecting}
        onClick={handleConnect}
      >
        {connecting ? "Connecting…" : "Connect Commonsku Payments"}
      </Button>
    </ForwardScreen>
  );

  const reviewScreen = (
    <ForwardScreen
      icon={<ClockIcon color={colors.neutrals["90"]} />}
      heading="Your application is being reviewed"
      description="We'll notify you by email once your account is approved, usually within 1–2 business days."
    />
  );

  const approvedBanner = (
    <Banner alertType="success">
      <BannerRow>
        <div>
          <Bold>commonsku payments is connected</Bold>
          <BannerDescription>
            View your transactions, payouts, and reconciliation reports in the
            Payments section.
          </BannerDescription>
        </div>
        <a
          href={PAYMENTS_DASHBOARD_URL}
          style={{ textDecoration: "none", flexShrink: 0 }}
        >
          <Button variant="secondary" size="medium">
            Go to payments dashboard
          </Button>
        </a>
      </BannerRow>
    </Banner>
  );

  const rejectedScreen = (heading: string) => (
    <ForwardScreen
      icon={<AlertIcon />}
      iconBackgroundColor={colors.errors["10"]}
      iconBorderColor={colors.errors["20"]}
      heading={heading}
      description="Contact our support team to find out next steps."
    >
      <a
        href={buildSupportMailto(applicationId)}
        style={{ textDecoration: "none" }}
      >
        <Button variant="primary" size="medium">
          Contact customer support
        </Button>
      </a>
    </ForwardScreen>
  );

  const notRightScreen = (
    <ForwardScreen
      icon={<AlertIcon />}
      iconBackgroundColor={colors.errors["10"]}
      iconBorderColor={colors.errors["20"]}
      heading="Something isn't right"
      description="We couldn't determine your commonsku payments application status. Please refresh, or contact support if this persists."
    />
  );

  const renderState = () => {
    // Boarding iframe takes over once a hosted link has been minted.
    if (boardingUri) {
      return (
        <BoardingIframe
          title="commonsku payments application"
          src={boardingUri}
        />
      );
    }
    if (refreshingAfterBoarding) {
      return <Loading />;
    }
    // Start flow: no application has been created yet.
    if (!applicationId) {
      return startScreen;
    }
    if (loading) {
      return <Loading />;
    }
    if (statusError) {
      return notRightScreen;
    }
    switch (group) {
      case FORWARD_GROUP.DRAFT:
        return startScreen;
      case FORWARD_GROUP.IN_REVIEW:
        return reviewScreen;
      case FORWARD_GROUP.APPROVED:
        return (
          <>
            {approvedBanner}
            <ForwardPaymentMethods
              data={data}
              onChange={(rails) => setPaymentCc(rails.payment_cc)}
            />
            <AdminPaymentCreditCardFee
              data={{ ...data, forward_payment_cc: paymentCc }}
              save={save}
            />
          </>
        );
      case FORWARD_GROUP.DECLINED:
        return rejectedScreen("Your application was rejected");
      case FORWARD_GROUP.CANCELLED:
        return rejectedScreen("Your application was cancelled");
      default:
        return notRightScreen;
    }
  };

  return (
    <div>
      {activeProvider && group !== FORWARD_GROUP.APPROVED && (
        <Banner alertType="neutral">
          <span>
            <Bold> {activeProvider} </Bold> is your active payment provider.{" "}
            <Bold> commonsku payments </Bold> will take over automatically once
            your account is set up.
          </span>
        </Banner>
      )}

      {renderState()}
    </div>
  );
}
